import { PitcherAgeBand } from './baseball.enums';

/**
 * USA Baseball Pitch Smart guidelines (https://www.mlb.com/pitch-smart/pitching-guidelines).
 * Daily maximum pitches by age band.
 */
export const PITCH_SMART_DAILY_MAX: Record<PitcherAgeBand, number> = {
  [PitcherAgeBand.Age7_8]: 50,
  [PitcherAgeBand.Age9_10]: 75,
  [PitcherAgeBand.Age11_12]: 85,
  [PitcherAgeBand.Age13_14]: 95,
  [PitcherAgeBand.Age15_16]: 95,
  [PitcherAgeBand.Age17_18]: 105,
  [PitcherAgeBand.Age19_22]: 120
};

export interface RestRule {
  /** Inclusive upper bound on pitches in day → required calendar rest days. */
  pitches: number;
  restDays: number;
}

export const PITCH_SMART_REST: Record<PitcherAgeBand, readonly RestRule[]> = {
  [PitcherAgeBand.Age7_8]: [
    { pitches: 20, restDays: 0 },
    { pitches: 35, restDays: 1 },
    { pitches: 50, restDays: 2 }
  ],
  [PitcherAgeBand.Age9_10]: [
    { pitches: 20, restDays: 0 },
    { pitches: 35, restDays: 1 },
    { pitches: 50, restDays: 2 },
    { pitches: 65, restDays: 3 },
    { pitches: 75, restDays: 4 }
  ],
  [PitcherAgeBand.Age11_12]: [
    { pitches: 20, restDays: 0 },
    { pitches: 35, restDays: 1 },
    { pitches: 50, restDays: 2 },
    { pitches: 65, restDays: 3 },
    { pitches: 85, restDays: 4 }
  ],
  [PitcherAgeBand.Age13_14]: [
    { pitches: 20, restDays: 0 },
    { pitches: 35, restDays: 1 },
    { pitches: 50, restDays: 2 },
    { pitches: 65, restDays: 3 },
    { pitches: 95, restDays: 4 }
  ],
  [PitcherAgeBand.Age15_16]: [
    { pitches: 30, restDays: 0 },
    { pitches: 45, restDays: 1 },
    { pitches: 60, restDays: 2 },
    { pitches: 75, restDays: 3 },
    { pitches: 95, restDays: 4 }
  ],
  [PitcherAgeBand.Age17_18]: [
    { pitches: 30, restDays: 0 },
    { pitches: 45, restDays: 1 },
    { pitches: 60, restDays: 2 },
    { pitches: 80, restDays: 3 },
    { pitches: 105, restDays: 4 }
  ],
  [PitcherAgeBand.Age19_22]: [
    { pitches: 30, restDays: 0 },
    { pitches: 45, restDays: 1 },
    { pitches: 60, restDays: 2 },
    { pitches: 80, restDays: 3 },
    { pitches: 105, restDays: 4 },
    { pitches: 120, restDays: 5 }
  ]
};

/**
 * Returns the number of calendar rest days required after throwing
 * `pitchCount` pitches at the given age band. Reads ascending and returns
 * the first rule whose `pitches` upper bound is >= pitchCount.
 */
export function requiredRestDays(ageBand: PitcherAgeBand, pitchCount: number): number {
  if (pitchCount <= 0) return 0;
  const ladder = PITCH_SMART_REST[ageBand];
  for (const rule of ladder) {
    if (pitchCount <= rule.pitches) return rule.restDays;
  }
  return ladder[ladder.length - 1].restDays;
}

/**
 * Maps an athlete age (years) to a Pitch Smart age band.
 */
export function ageToBand(age: number): PitcherAgeBand {
  if (age <= 8) return PitcherAgeBand.Age7_8;
  if (age <= 10) return PitcherAgeBand.Age9_10;
  if (age <= 12) return PitcherAgeBand.Age11_12;
  if (age <= 14) return PitcherAgeBand.Age13_14;
  if (age <= 16) return PitcherAgeBand.Age15_16;
  if (age <= 18) return PitcherAgeBand.Age17_18;
  return PitcherAgeBand.Age19_22;
}
